// =====================================================================
// SocketStatusBanner (S39 fix #47) — banner global que aparece quando
// a conexao Socket.io cai e nao reconecta. Avisa atendente pra
// recarregar a pagina (Ctrl+F5).
//
// Escuta:
//   - custom event 'socket-reconnect-failed' (vem do SocketWorker quando
//     todas tentativas de reconnect esgotaram)
//   - window 'offline' / 'online' nativos do browser
//
// Self-dismissable + botao recarregar manual.
// =====================================================================
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import WifiOffIcon from "@material-ui/icons/WifiOff";

const useStyles = makeStyles((theme) => ({
  banner: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: "#dc2626",
    color: "white",
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    fontSize: "0.95rem",
    fontWeight: 500
  },
  warning: {
    background: "#f59e0b"
  },
  message: {
    flex: 1
  },
  reloadBtn: {
    background: "white",
    color: "#dc2626",
    fontWeight: 700,
    "&:hover": {
      background: "#fee"
    }
  },
  reloadBtnWarn: {
    color: "#92400e"
  },
  closeBtn: {
    color: "white"
  }
}));

const SocketStatusBanner = () => {
  const classes = useStyles();
  const [state, setState] = useState({
    visible: false,
    type: "warning", // warning | error
    message: ""
  });

  const showBanner = (type, message) => {
    setState({ visible: true, type, message });
  };

  const hideBanner = () => {
    setState((s) => ({ ...s, visible: false }));
  };

  useEffect(() => {
    const onReconnectFailed = () => {
      showBanner(
        "error",
        "⚠️ Conexão perdida com o servidor. Atualize a página pra continuar recebendo mensagens em tempo real."
      );
    };

    // S53: mismatch de empresa persistente (2 contas no mesmo navegador) -> SocketWorker
    // dispara 'socket-loop-detected' e PARA de reconectar. Aqui avisamos pra re-login.
    const onLoopDetected = () => {
      showBanner(
        "error",
        "⚠️ Sessão inconsistente com o servidor (possível login em duas contas no mesmo navegador). Saia e entre novamente pra voltar ao tempo real."
      );
    };

    const onOffline = () => {
      showBanner(
        "warning",
        "📵 Sem internet. Mensagens novas vão aparecer quando a conexão voltar."
      );
    };

    const onOnline = () => {
      // Volta online — tenta reconectar e esconde banner se reconnect_failed
      // ainda nao disparou. Espera 3s antes de esconder pra dar tempo de
      // realmente reconectar.
      setTimeout(() => {
        if (state.type === "warning") {
          hideBanner();
        }
      }, 3000);
    };

    window.addEventListener("socket-reconnect-failed", onReconnectFailed);
    window.addEventListener("socket-loop-detected", onLoopDetected);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("socket-reconnect-failed", onReconnectFailed);
      window.removeEventListener("socket-loop-detected", onLoopDetected);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state.visible) return null;

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      className={`${classes.banner} ${state.type === "warning" ? classes.warning : ""}`}
      role="alert"
    >
      <WifiOffIcon style={{ flexShrink: 0 }} />
      <span className={classes.message}>{state.message}</span>
      {state.type === "error" && (
        <Button
          size="small"
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleReload}
          className={classes.reloadBtn}
        >
          Recarregar agora
        </Button>
      )}
      <IconButton
        size="small"
        onClick={hideBanner}
        className={classes.closeBtn}
        aria-label="Fechar aviso"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default SocketStatusBanner;
